import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, BookOpen, CheckCircle, BedDouble, Bell, ArrowRight, Clock, TrendingUp, Zap, X, Wifi, BedSingle, Bot, PartyPopper } from 'lucide-react';
import api from '../utils/api';
import { useSocket } from '../context/SocketContext';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';

function OnboardingChecklist({ onboarding, onDismiss }) {
  const navigate = useNavigate();
  if (!onboarding || onboarding.dismissed) return null;

  const steps = [
    {
      label: 'Hesap oluşturuldu',
      done: true,
      icon: CheckCircle,
      color: '#22c55e',
      action: null,
    },
    {
      label: 'WhatsApp bağlandı',
      done: onboarding.whatsappConnected,
      icon: Wifi,
      color: '#6366f1',
      action: { label: 'Bağla →', to: '/settings?tab=channels' },
    },
    {
      label: 'Oda/fiyat bilgileri eklendi',
      done: onboarding.hasRooms,
      icon: BedSingle,
      color: '#f59e0b',
      action: { label: 'Ekle →', to: '/settings?tab=rooms' },
    },
    {
      label: 'Bot test edildi',
      done: onboarding.botTested,
      icon: Bot,
      color: '#8b5cf6',
      action: { label: 'Test Et →', to: '/bot-control' },
    },
  ];

  const completedCount = steps.filter(s => s.done).length;
  const allDone = completedCount === steps.length;

  return (
    <div style={{
      background: allDone
        ? 'linear-gradient(135deg, #f0fdf4, #dcfce7)'
        : 'linear-gradient(135deg, #faf5ff, #ede9fe)',
      border: `1px solid ${allDone ? '#86efac' : '#c4b5fd'}`,
      borderRadius: 16,
      padding: '18px 22px',
      marginBottom: 24,
      position: 'relative',
    }}>
      {/* Başlık */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {allDone
            ? <PartyPopper size={20} color="#16a34a" />
            : <div style={{ fontSize: 13, fontWeight: 700, color: '#6366f1' }}>Kurulum Adımları</div>}
          {allDone
            ? <span style={{ fontSize: 14, fontWeight: 700, color: '#15803d' }}>Kurulum tamam! 🎉 Her şey hazır.</span>
            : (
              <div style={{
                background: '#6366f1', color: '#fff', borderRadius: 999,
                padding: '2px 10px', fontSize: 12, fontWeight: 700,
              }}>
                {completedCount}/{steps.length}
              </div>
            )}
        </div>
        {allDone && (
          <button
            onClick={onDismiss}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: 4, borderRadius: 6, display: 'flex' }}
            title="Kapat"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* İlerleme çubuğu */}
      {!allDone && (
        <div style={{ height: 4, background: '#e9d5ff', borderRadius: 99, marginBottom: 16, overflow: 'hidden' }}>
          <div style={{
            height: '100%',
            width: `${(completedCount / steps.length) * 100}%`,
            background: 'linear-gradient(90deg, #6366f1, #8b5cf6)',
            borderRadius: 99,
            transition: 'width 0.5s ease',
          }} />
        </div>
      )}

      {/* Adımlar */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {steps.map((step, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: step.done ? '#fff' : '#fff',
            border: `1px solid ${step.done ? '#d1fae5' : '#e2e8f0'}`,
            borderRadius: 10, padding: '8px 12px',
            opacity: step.done ? 1 : 0.9,
            flex: '1 1 180px', minWidth: 0,
          }}>
            <div style={{
              width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
              background: step.done ? '#dcfce7' : '#f1f5f9',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {step.done
                ? <CheckCircle size={15} color="#22c55e" />
                : <step.icon size={13} color="#94a3b8" />}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: 12, fontWeight: 600,
                color: step.done ? '#15803d' : '#475569',
                textDecoration: step.done ? 'none' : 'none',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>
                {step.label}
              </div>
            </div>
            {!step.done && step.action && (
              <button
                onClick={() => navigate(step.action.to)}
                style={{
                  background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                  color: '#fff', border: 'none', borderRadius: 6,
                  padding: '3px 9px', fontSize: 11, fontWeight: 700,
                  cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
                }}
              >
                {step.action.label}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function GradientStatCard({ icon: Icon, label, value, gradient, iconBg }) {
  return (
    <div style={{
      background: gradient, borderRadius: 18, padding: '22px 24px',
      color: 'white', position: 'relative', overflow: 'hidden',
      boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
    }}>
      <div style={{ position: 'absolute', top: -20, right: -20, width: 100, height: 100, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
      <div style={{ position: 'absolute', bottom: -30, right: 20, width: 80, height: 80, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', position: 'relative' }}>
        <div>
          <div style={{ fontSize: 36, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1 }}>{value}</div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.8)', marginTop: 6, fontWeight: 500 }}>{label}</div>
        </div>
        <div style={{ width: 46, height: 46, borderRadius: 14, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={22} color="white" />
        </div>
      </div>
    </div>
  );
}

function ChannelBadge({ channel }) {
  const isIG = channel === 'INSTAGRAM';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 9px', borderRadius: 999,
      background: isIG ? '#dbeafe' : '#dcfce7',
      color: isIG ? '#1d4ed8' : '#15803d',
      fontSize: 11, fontWeight: 700,
    }}>
      {isIG ? '📸' : '💬'} {channel}
    </span>
  );
}

export default function DashboardPage() {
  const [stats, setStats] = useState({ activeConversations: 0, pendingReservations: 0, confirmedToday: 0, totalRooms: 0 });
  const [onboarding, setOnboarding] = useState(null);
  const [pendingReservations, setPendingReservations] = useState([]);
  const [recentConversations, setRecentConversations] = useState([]);
  const socket = useSocket();
  const navigate = useNavigate();
  const { hotel } = useAuth();
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Günaydın' : hour < 18 ? 'İyi günler' : 'İyi akşamlar';

  const fetchData = async () => {
    try {
      const [statsData, reservationsData, conversationsData] = await Promise.all([
        api.get('/hotels/dashboard'),
        api.get('/reservations?status=PENDING&limit=5'),
        api.get('/conversations?status=ACTIVE&limit=5'),
      ]);
      setStats(statsData);
      setOnboarding(statsData.onboarding || null);
      setPendingReservations(reservationsData.reservations || []);
      setRecentConversations(conversationsData.conversations || []);
    } catch (err) {
      console.error('Dashboard yüklenemedi:', err);
    }
  };

  const handleDismissOnboarding = async () => {
    try {
      await api.post('/hotels/onboarding/dismiss');
      setOnboarding(prev => prev ? { ...prev, dismissed: true } : prev);
    } catch {
      // sessizce devam
    }
  };

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    if (!socket) return;
    socket.on('new-reservation', (data) => {
      toast.success(`Yeni rezervasyon talebi: ${data.customerName}`, { icon: '📋' });
      fetchData();
    });
    socket.on('new-message', () => fetchData());
    return () => { socket.off('new-reservation'); socket.off('new-message'); };
  }, [socket]);

  const handleConfirm = async (id) => {
    try { await api.post(`/reservations/${id}/confirm`); toast.success('Rezervasyon onaylandı'); fetchData(); }
    catch { toast.error('Hata oluştu'); }
  };
  const handleReject = async (id) => {
    try { await api.post(`/reservations/${id}/reject`, { reason: 'Müsait değil' }); toast.success('Rezervasyon reddedildi'); fetchData(); }
    catch { toast.error('Hata oluştu'); }
  };

  return (
    <div style={{ padding: 28, maxWidth: 1300 }}>
      {/* Onboarding şeridi */}
      <OnboardingChecklist onboarding={onboarding} onDismiss={handleDismissOnboarding} />

      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h1 style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.02em', color: '#1e293b' }}>
              {greeting} 👋
            </h1>
            <p style={{ fontSize: 14, color: '#64748b', marginTop: 4 }}>
              {hotel?.name || 'Otel'} · {format(new Date(), 'd MMMM yyyy, EEEE', { locale: tr })}
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#dcfce7', borderRadius: 12, padding: '8px 14px' }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e' }} className="pulse-dot" />
            <span style={{ fontSize: 13, fontWeight: 600, color: '#15803d' }}>AI Asistan Aktif</span>
          </div>
        </div>
      </div>

      {/* Stat Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 18, marginBottom: 28 }}>
        <GradientStatCard icon={MessageSquare} label="Aktif Konuşma" value={stats.activeConversations} gradient="linear-gradient(135deg, #6366f1, #818cf8)" />
        <GradientStatCard icon={Bell} label="Onay Bekleyen" value={stats.pendingReservations} gradient="linear-gradient(135deg, #f59e0b, #f97316)" />
        <GradientStatCard icon={CheckCircle} label="Bugün Onaylanan" value={stats.confirmedToday} gradient="linear-gradient(135deg, #22c55e, #10b981)" />
        <GradientStatCard icon={BedDouble} label="Toplam Oda" value={stats.totalRooms} gradient="linear-gradient(135deg, #ec4899, #f43f5e)" />
      </div>

      {/* Quick Actions */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 28 }}>
        {[
          { icon: Zap, label: 'Bot Test Et', sub: 'AI asistanı dene', to: '/bot-control', color: '#6366f1' },
          { icon: TrendingUp, label: 'Raporları Gör', sub: 'Analitik & istatistikler', to: '/reports', color: '#3b82f6' },
          { icon: Clock, label: 'Takvimi Aç', sub: 'Rezervasyon takvimi', to: '/calendar', color: '#22c55e' },
        ].map(item => (
          <div key={item.to} onClick={() => navigate(item.to)} style={{
            background: 'white', borderRadius: 14, padding: '16px 18px', cursor: 'pointer',
            border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 14,
            transition: 'all 0.2s', boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
          }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.1)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.04)'; }}
          >
            <div style={{ width: 42, height: 42, borderRadius: 12, background: item.color + '15', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <item.icon size={19} color={item.color} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b' }}>{item.label}</div>
              <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 1 }}>{item.sub}</div>
            </div>
            <ArrowRight size={16} color="#cbd5e1" />
          </div>
        ))}
      </div>

      {/* Main Content */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Pending Reservations */}
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
            <div>
              <h2 style={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>Onay Bekleyen</h2>
              <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>Rezervasyon talepleri</p>
            </div>
            {pendingReservations.length > 0 && (
              <span style={{ background: 'linear-gradient(135deg, #f59e0b, #f97316)', color: 'white', borderRadius: 999, padding: '3px 10px', fontSize: 12, fontWeight: 700 }}>
                {pendingReservations.length}
              </span>
            )}
          </div>

          {pendingReservations.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px 0', color: '#94a3b8' }}>
              <CheckCircle size={36} color="#dcfce7" style={{ margin: '0 auto 10px' }} />
              <div style={{ fontSize: 14, fontWeight: 500 }}>Bekleyen rezervasyon yok</div>
              <div style={{ fontSize: 12, marginTop: 4 }}>Tüm talepler yanıtlandı</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {pendingReservations.map(r => (
                <div key={r.id} style={{
                  border: '1px solid #f1f5f9', borderRadius: 12, padding: '14px 16px',
                  background: '#fafbff', transition: 'all 0.2s',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b' }}>{r.customerName}</div>
                      <div style={{ fontSize: 12, color: '#64748b', marginTop: 3 }}>
                        📅 {format(new Date(r.checkIn), 'd MMM', { locale: tr })} → {format(new Date(r.checkOut), 'd MMM yyyy', { locale: tr })}
                        {' · '}{r.adults} yetişkin{r.children > 0 ? `, ${r.children} çocuk` : ''}
                      </div>
                      {r.room && <div style={{ fontSize: 12, color: '#6366f1', marginTop: 3, fontWeight: 600 }}>🛏️ {r.room.name}</div>}
                    </div>
                    <ChannelBadge channel={r.channel} />
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                    <button className="btn btn-success" style={{ fontSize: 12, padding: '6px 14px', flex: 1 }} onClick={() => handleConfirm(r.id)}>✓ Onayla</button>
                    <button className="btn btn-danger" style={{ fontSize: 12, padding: '6px 14px' }} onClick={() => handleReject(r.id)}>✕</button>
                    <button className="btn btn-secondary" style={{ fontSize: 12, padding: '6px 14px' }} onClick={() => navigate(`/conversations/${r.conversationId}`)}>
                      <MessageSquare size={13} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Active Conversations */}
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
            <div>
              <h2 style={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>Canlı Konuşmalar</h2>
              <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>Bot ile aktif görüşmeler</p>
            </div>
            <button onClick={() => navigate('/conversations')} style={{
              display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none',
              color: '#6366f1', cursor: 'pointer', fontSize: 13, fontWeight: 600,
            }}>
              Tümü <ArrowRight size={14} />
            </button>
          </div>

          {recentConversations.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px 0', color: '#94a3b8' }}>
              <MessageSquare size={36} color="#e2e8f0" style={{ margin: '0 auto 10px' }} />
              <div style={{ fontSize: 14, fontWeight: 500 }}>Aktif konuşma yok</div>
              <div style={{ fontSize: 12, marginTop: 4 }}>Yeni mesaj geldiğinde burada görünür</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {recentConversations.map(c => (
                <div key={c.id} onClick={() => navigate(`/conversations/${c.id}`)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px',
                    borderRadius: 12, border: '1px solid #f1f5f9', cursor: 'pointer',
                    transition: 'all 0.2s', background: '#fafbff',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#ede9fe20'; e.currentTarget.style.borderColor = '#c4b5fd'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = '#fafbff'; e.currentTarget.style.borderColor = '#f1f5f9'; }}
                >
                  <div style={{
                    width: 38, height: 38, borderRadius: '50%',
                    background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 15, fontWeight: 700, color: 'white', flexShrink: 0,
                  }}>
                    {(c.customerName || '?')[0].toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, color: '#1e293b' }}>{c.customerName || 'Bilinmeyen'}</div>
                    <div style={{ fontSize: 12, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {c.messages?.[0]?.content || 'Mesaj yok'}
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
                    <ChannelBadge channel={c.channel} />
                    {c.isTakenOver && <span className="badge badge-yellow" style={{ fontSize: 10 }}>Devralındı</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
